import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { collection, query, where, onSnapshot, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

export default function Reviews({ imdbID }) {
  const { currentUser, loginWithGoogle } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!imdbID) return;
    const q = query(
      collection(db, "reviews"),
      where("imdbID", "==", imdbID)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const revs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      revs.sort((a, b) => {
        const tA = a.timestamp?.toMillis() || 0;
        const tB = b.timestamp?.toMillis() || 0;
        return tB - tA;
      });
      setReviews(revs);
    });
    return unsubscribe;
  }, [imdbID]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser || !newReview.trim() || rating === 0) return;
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "reviews"), {
        imdbID,
        userId: currentUser.uid,
        userName: currentUser.displayName || "Anonymous User",
        userPhoto: currentUser.photoURL || "",
        text: newReview.trim(),
        rating,
        timestamp: serverTimestamp()
      });
      setNewReview("");
      setRating(0);
      setHoverRating(0);
    } catch (err) {
      console.error("Error adding review: ", err);
      alert("Failed to submit review. Check console for details.");
    }
    setIsSubmitting(false);
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : null;

  const StarIcon = ({ filled, size = 16, onClick, onMouseEnter, onMouseLeave, interactive = false }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      fill={filled ? "#e7b816" : "rgba(255,255,255,0.2)"}
      viewBox="0 0 16 16"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        cursor: interactive ? "pointer" : "default",
        transition: "all 0.15s ease",
        filter: filled ? "drop-shadow(0 0 4px rgba(231,184,22,0.5))" : "none",
        transform: interactive && filled ? "scale(1.1)" : "scale(1)"
      }}
    >
      <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z" />
    </svg>
  );

  const renderStars = (count, size = 14) => (
    <div className="d-flex align-items-center" style={{ gap: "3px" }}>
      {[1, 2, 3, 4, 5].map(i => (
        <StarIcon key={i} filled={i <= count} size={size} />
      ))}
    </div>
  );

  const containerStyle = {
    background: "linear-gradient(135deg, rgba(15,7,22,0.9) 0%, rgba(30,15,45,0.8) 100%)",
    border: "1px solid rgba(231,184,22,0.15)",
    borderRadius: "20px",
    backdropFilter: "blur(20px)",
  };

  const cardStyle = {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    transition: "all 0.3s ease",
  };

  return (
    <div className="mt-5 p-4 p-md-5 text-white" style={containerStyle}>
      {/* Header with count & average */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 pb-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <div>
          <h3 className="mb-1 fw-bold" style={{ letterSpacing: "-0.5px" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="var(--accent)" className="me-2 mb-1" viewBox="0 0 16 16">
              <path d="M2.678 11.894a1 1 0 0 1 .287.801 11 11 0 0 1-.398 2c1.395-.323 2.247-.697 2.634-.893a1 1 0 0 1 .71-.074A8 8 0 0 0 8 14c3.996 0 7-2.807 7-6 0-3.192-3.004-6-7-6S1 4.808 1 8c0 1.468.617 2.83 1.678 3.894m-.493 3.905a22 22 0 0 1-.713.129c-.2.032-.352-.176-.273-.362a10 10 0 0 0 .244-.637l.003-.01c.248-.72.45-1.548.524-2.319C.743 11.37 0 9.76 0 8c0-3.866 3.582-7 8-7s8 3.134 8 7-3.582 7-8 7a9 9 0 0 1-2.347-.306c-.52.263-1.639.742-3.468 1.105"/>
            </svg>
            User Reviews
          </h3>
          <span className="text-white-50 small">{reviews.length} review{reviews.length !== 1 ? "s" : ""}</span>
        </div>
        {avgRating && (
          <div className="d-flex align-items-center mt-2 mt-sm-0 px-3 py-2 rounded-pill" style={{ background: "rgba(231,184,22,0.1)", border: "1px solid rgba(231,184,22,0.25)" }}>
            <span className="fw-bold me-2" style={{ color: "var(--accent)", fontSize: "1.3rem" }}>{avgRating}</span>
            {renderStars(Math.round(parseFloat(avgRating)))}
          </div>
        )}
      </div>

      {/* Write a Review / Sign-In Prompt */}
      {currentUser ? (
        <motion.form
          onSubmit={handleSubmit}
          className="mb-5 p-4 rounded-4"
          style={{ ...cardStyle, background: "rgba(0,0,0,0.35)" }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="d-flex align-items-center mb-4">
            <img
              src={currentUser.photoURL || "https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png"}
              alt={currentUser.displayName}
              width="40" height="40"
              className="rounded-circle me-3 border border-2"
              style={{ borderColor: "var(--accent)" }}
              referrerPolicy="no-referrer"
            />
            <div>
              <h6 className="mb-0 fw-bold">{currentUser.displayName}</h6>
              <span className="small text-white-50">Writing a review</span>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: "8px" }}>
            <span className="text-white-50 small text-uppercase fw-bold me-2" style={{ letterSpacing: "1px" }}>Your Rating</span>
            <div className="d-flex" style={{ gap: "6px" }}>
              {[1, 2, 3, 4, 5].map(num => (
                <StarIcon
                  key={num}
                  filled={num <= (hoverRating || rating)}
                  size={28}
                  interactive
                  onClick={() => setRating(num)}
                  onMouseEnter={() => setHoverRating(num)}
                  onMouseLeave={() => setHoverRating(0)}
                />
              ))}
            </div>
            {rating > 0 && <span className="badge rounded-pill ms-2" style={{ background: "rgba(231,184,22,0.2)", color: "var(--accent)" }}>{rating}/5</span>}
          </div>

          <textarea
            className="form-control border-0 shadow-none mb-3"
            rows="3"
            placeholder="Share your experience with this title..."
            value={newReview}
            onChange={(e) => setNewReview(e.target.value)}
            style={{
              backgroundColor: "rgba(255,255,255,0.05)",
              color: "white",
              borderRadius: "12px",
              padding: "16px",
              resize: "vertical",
              fontSize: "0.95rem",
              lineHeight: "1.6"
            }}
          />

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn rounded-pill px-4 py-2 fw-bold d-flex align-items-center"
              style={{
                background: (isSubmitting || !newReview.trim() || rating === 0) ? "rgba(231,184,22,0.3)" : "var(--accent)",
                color: (isSubmitting || !newReview.trim() || rating === 0) ? "rgba(0,0,0,0.5)" : "#000",
                border: "none",
                transition: "all 0.3s ease"
              }}
              disabled={isSubmitting || !newReview.trim() || rating === 0}
            >
              {isSubmitting ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" />Posting...</>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                    <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z" />
                  </svg>
                  Post Review
                </>
              )}
            </button>
          </div>
        </motion.form>
      ) : (
        <motion.div
          className="mb-5 p-4 p-md-5 text-center d-flex flex-column align-items-center rounded-4"
          style={{ ...cardStyle, background: "rgba(0,0,0,0.3)", border: "1px dashed rgba(231,184,22,0.3)" }}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mb-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px", background: "rgba(231,184,22,0.1)" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="var(--accent)" viewBox="0 0 16 16">
              <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10s-3.516.68-4.168 1.332c-.678.678-.83 1.418-.832 1.664z"/>
            </svg>
          </div>
          <h5 className="mb-2 fw-bold">Join the Conversation</h5>
          <p className="text-white-50 mb-4 small" style={{ maxWidth: '360px', lineHeight: '1.7' }}>Sign in to leave a review, rate movies, and share your opinions with the community.</p>
          <button className="btn btn-warning rounded-pill px-4 py-2 fw-bold shadow d-flex align-items-center" onClick={loginWithGoogle}>
            <svg className="me-2" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
              <path d="M15.545 6.558a9.42 9.42 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.689 7.689 0 0 1 5.352 2.082l-2.284 2.284A4.347 4.347 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.792 4.792 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.702 3.702 0 0 0 1.599-2.431H8v-3.08h7.545z"/>
            </svg>
            Sign In with Google
          </button>
        </motion.div>
      )}

      {/* Reviews List */}
      <AnimatePresence>
        {reviews.length === 0 ? (
          <motion.div
            className="text-center py-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="rgba(255,255,255,0.15)" viewBox="0 0 16 16">
                <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"/>
              </svg>
            </div>
            <p className="text-white-50 fst-italic fs-6 mb-0">No reviews yet. Be the first to share your thoughts!</p>
          </motion.div>
        ) : (
          <div className="d-flex flex-column" style={{ gap: "16px" }}>
            {reviews.map((rev, index) => (
              <motion.div
                key={rev.id}
                className="p-3 p-md-4"
                style={cardStyle}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                  e.currentTarget.style.borderColor = "rgba(231,184,22,0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                }}
              >
                <div className="d-flex">
                  <img
                    src={rev.userPhoto || "https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png"}
                    alt="user avatar"
                    className="rounded-circle border border-secondary flex-shrink-0"
                    width="48" height="48"
                    style={{ objectFit: 'cover' }}
                    referrerPolicy="no-referrer"
                  />
                  <div className="ms-3 w-100" style={{ minWidth: 0 }}>
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-1">
                      <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                        <span className="fw-bold" style={{ fontSize: "1rem" }}>{rev.userName}</span>
                        {rev.timestamp && (
                          <span className="text-white-50" style={{ fontSize: "0.8rem" }}>
                            {rev.timestamp.toDate().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 mt-sm-0">{renderStars(rev.rating)}</div>
                    </div>
                    <p className="mb-0 mt-2 text-white-50" style={{ whiteSpace: "pre-wrap", lineHeight: '1.7', fontSize: "0.95rem" }}>{rev.text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
